import React from 'react';
import { useIntl } from 'react-intl';
import KeyboardReact, { KeyboardReactInterface } from 'react-simple-keyboard';

import { BUTTON_LABELS, OPTIONS_BY_LANG } from '../../utils/KeyUtils';
import shiftLock from 'components/Keyboard/modules/shiftLock';
import candidatesModule from 'components/Keyboard/modules/candidates';
import getJapaneseCandidates from 'components/Keyboard/Custom/Japanese/layouts/provider';
import { DEFAULT_THEME, SPECIAL_KEYS } from 'components/Keyboard/utils/constants';

import qwertyLayout from './layouts/qwerty';
import { DEFAULT_BUTTON_THEME } from '../Default';
import type { CandidatesKeyboard, KeyboardComponentProps } from 'components/Keyboard/types';

// Full-frame QWERTY keyboard with Japanese IME support: the candidates module converts the
// Romaji being typed into Hiragana/Katakana/Kanji suggestions shown in the candidate bar.
const MODULES = [candidatesModule(getJapaneseCandidates), shiftLock];

// The IME candidates are provider-driven (getJapaneseCandidates), so the static
// layoutCandidates / layoutCandidatesCaseSensitiveMatch options are never consulted here.
// Only layoutCandidatesPageSize matters: it sizes the candidate box pagination.
type QwertyKeyboardProps = Omit<KeyboardComponentProps, 'layoutType' | 'layoutCandidates'> & {
  onToggle: () => void;
};

const QwertyKeyboard: React.FC<QwertyKeyboardProps> = ({
  type,
  language,
  onInit,
  onChange,
  keyboardRef,
  onKeyReleased,
  onSelectionChange,
  onToggle,
}) => {
  const intl = useIntl();
  const display = React.useMemo(() => BUTTON_LABELS(intl), [intl]);

  const layoutCandidatesPageSize = React.useMemo(
    () => OPTIONS_BY_LANG[language].layoutCandidatesPageSize,
    [language],
  );

  const mainKeyboardRef = React.useRef<CandidatesKeyboard | null>(null);

  const setMainRef = React.useCallback(
    (ref: KeyboardReactInterface) => {
      mainKeyboardRef.current = ref as CandidatesKeyboard;
      keyboardRef(ref);
    },
    [keyboardRef],
  );

  // The globe key flushes any pending candidates and switches back to the Japanese keyboard.
  const handleKeyPress = React.useCallback(
    (button: string) => {
      if (button === SPECIAL_KEYS.NEXT_KEYBOARD) {
        mainKeyboardRef.current?.flushCandidates();
        onToggle();
      }
    },
    [onToggle],
  );

  return (
    <KeyboardReact
      type={type}
      mergeDisplay
      newLineOnEnter
      onInit={onInit}
      display={display}
      onChange={onChange}
      language={language}
      layout={qwertyLayout}
      modules={MODULES}
      keyboardRef={setMainRef}
      onKeyReleased={onKeyReleased}
      onKeyPress={handleKeyPress}
      buttonTheme={DEFAULT_BUTTON_THEME}
      onSelectionChange={onSelectionChange}
      layoutCandidatesPageSize={layoutCandidatesPageSize}
      theme={`${DEFAULT_THEME} fb-keyboard paginated-candidates`}
    />
  );
};

export default QwertyKeyboard;
